import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../components/profile-components/Navbar';
import Topbar from '../components/profile-components/Topbar';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import './SalesPages.css';
import { authFetch } from '../utils/authFetch';

const escapeCsvValue = (value) => {
  const stringValue = value === null || value === undefined ? '' : String(value);
  return `"${stringValue.replace(/"/g, '""')}"`;
};

const downloadCsv = (filename, rows) => {
  if (!rows.length) return;

  const headers = Object.keys(rows[0]);
  const csv = [
    headers.map(escapeCsvValue).join(','),
    ...rows.map((row) => headers.map((header) => escapeCsvValue(row[header])).join(',')),
  ].join('\n');

  const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

function Clients() {
  const [clients, setClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [coverageFilter, setCoverageFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [exportOpen, setExportOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const loadClients = async () => {
      try {
        setLoading(true);

        const res = await authFetch('/api/clients');

        if (!res.ok) {
          throw new Error('Failed to load clients');
        }

        const data = await res.json();

        if (Array.isArray(data)) {
          setClients(data);
        } else {
          console.error('Unexpected clients response:', data);
          setClients([]);
        }
      } catch (err) {
        console.error('Error fetching clients:', err);
        setClients([]);
      } finally {
        setLoading(false);
      }
    };

    loadClients();
  }, []);

  const getCoverageType = (policy) =>
    policy?.coverageTypeName ||
    policy?.coverage_type_name ||
    policy?.coverage_type ||
    policy?.coverageType ||
    '—';

  const getPolicyStatus = (policy) =>
    policy?.status ||
    policy?.policy_status ||
    policy?.status_label ||
    '—';

  const getEffectiveDate = (policy) =>
    policy?.effectiveDate ||
    policy?.effective_date ||
    '—';

  const getTerminationDate = (policy) =>
    policy?.terminationDate ||
    policy?.termination_date ||
    '—';

  const filteredClients = useMemo(() => {
    return clients.filter((c) => {
      const matchesCoverage =
        !coverageFilter ||
        c.policies?.some((p) => getCoverageType(p) === coverageFilter);

      const matchesStatus =
        !statusFilter ||
        c.policies?.some((p) => getPolicyStatus(p) === statusFilter);

      const matchesSearch =
        !searchTerm ||
        `${c.first_name} ${c.last_name}`.toLowerCase().includes(searchTerm.toLowerCase());

      return matchesCoverage && matchesStatus && matchesSearch;
    });
  }, [clients, coverageFilter, statusFilter, searchTerm]);

  const uniqueCoverageTypes = Array.from(
    new Set(clients.flatMap((c) => c.policies?.map((p) => getCoverageType(p))).filter(Boolean))
  );

  const uniqueStatuses = Array.from(
    new Set(clients.flatMap((c) => c.policies?.map((p) => getPolicyStatus(p))).filter(Boolean))
  );

  const statusClass = (value) => {
    const normalized = String(value || '')
      .toLowerCase()
      .replace(/\s+/g, '_')
      .replace(/\//g, '_')
      .replace(/-+/g, '_');

    return `sales-chip ${normalized || 'sales-chip-neutral'}`;
  };

  const exportCSV = () => {
    downloadCsv(
      'clients.csv',
      filteredClients.map((c) => ({
        Name: `${c.first_name || ''} ${c.last_name || ''}`.trim(),
        'Coverage Type': c.policies?.map((p) => getCoverageType(p)).join(' | ') || '-',
        'Policy Status': c.policies?.map((p) => getPolicyStatus(p)).join(' | ') || '-',
        'Effective Date': c.policies?.map((p) => getEffectiveDate(p)).join(' | ') || '-',
        'Termination Date': c.policies?.map((p) => getTerminationDate(p)).join(' | ') || '-',
        Phone: c.phone || '',
        Email: c.email || '',
      }))
    );

    setExportOpen(false);
  };

  const exportPDF = () => {
    const doc = new jsPDF();

    autoTable(doc, {
      head: [[
        'Client Name',
        'Coverage Type',
        'Policy Status',
        'Effective Date',
        'Termination Date',
        'Phone',
        'Email',
      ]],
      body: filteredClients.map((c) => [
        `${c.first_name || ''} ${c.last_name || ''}`.trim(),
        c.policies?.map((p) => getCoverageType(p)).join(' | ') || '-',
        c.policies?.map((p) => getPolicyStatus(p)).join(' | ') || '-',
        c.policies?.map((p) => getEffectiveDate(p)).join(' | ') || '-',
        c.policies?.map((p) => getTerminationDate(p)).join(' | ') || '-',
        c.phone || '',
        c.email || '',
      ]),
      styles: { fontSize: 8 },
    });

    doc.save('clients.pdf');
    setExportOpen(false);
  };

  return (
    <>
      <Navbar />
      <Topbar />

      <div className="main-content sales-page">
        <div className="sales-hero">
          <div>
            <p className="sales-eyebrow">Agent Book CRM</p>
            <h1 className="sales-title">Clients</h1>
            <p className="sales-subtitle">
              Manage active client relationships, policy coverage, and key contact details.
            </p>
          </div>

          <button
            onClick={() => navigate('/new-client')}
            className="sales-primary-btn"
          >
            + Add Client
          </button>
        </div>

        <div className="sales-toolbar-card">
          <div
            style={{
              display: 'flex',
              gap: '10px',
              flexWrap: 'wrap',
              alignItems: 'center',
            }}
          >
            <select
              value={coverageFilter}
              onChange={(e) => setCoverageFilter(e.target.value)}
              className="sales-search-input"
              style={{ maxWidth: '240px' }}
            >
              <option value="">All Coverage Types</option>
              {uniqueCoverageTypes.map((ct) => (
                <option key={ct} value={ct}>
                  {ct}
                </option>
              ))}
            </select>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="sales-search-input"
              style={{ maxWidth: '220px' }}
            >
              <option value="">All Policy Status</option>
              {uniqueStatuses.map((st) => (
                <option key={st} value={st}>
                  {st}
                </option>
              ))}
            </select>

            <input
              type="text"
              className="sales-search-input"
              placeholder="Search clients by name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ minWidth: '260px', flex: 1 }}
            />

            <div style={{ position: 'relative', marginLeft: 'auto' }}>
              <button
                onClick={() => setExportOpen(!exportOpen)}
                className="sales-secondary-btn"
                type="button"
              >
                Export Clients ▾
              </button>

              {exportOpen && (
                <div
                  style={{
                    position: 'absolute',
                    top: 'calc(100% + 8px)',
                    right: 0,
                    minWidth: '160px',
                    background: '#fff',
                    border: '1px solid rgba(226, 232, 240, 0.95)',
                    borderRadius: '14px',
                    boxShadow: '0 10px 24px rgba(15, 23, 42, 0.12)',
                    overflow: 'hidden',
                    zIndex: 50,
                  }}
                >
                  <button
                    onClick={exportCSV}
                    type="button"
                    style={{
                      display: 'block',
                      width: '100%',
                      padding: '10px 12px',
                      border: 'none',
                      background: '#fff',
                      textAlign: 'left',
                      cursor: 'pointer',
                    }}
                  >
                    Export as CSV
                  </button>
                  <button
                    onClick={exportPDF}
                    type="button"
                    style={{
                      display: 'block',
                      width: '100%',
                      padding: '10px 12px',
                      border: 'none',
                      background: '#fff',
                      textAlign: 'left',
                      cursor: 'pointer',
                    }}
                  >
                    Export as PDF
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="sales-table-card">
          <div className="sales-table-header">
            <div>
              <h2>All Clients</h2>
              <p>
                {filteredClients.length} client{filteredClients.length === 1 ? '' : 's'}
              </p>
            </div>
          </div>

          <div className="sales-table-wrap">
            <table className="sales-table">
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Coverage Type</th>
                  <th>Policy Status</th>
                  <th>Effective Date</th>
                  <th>Termination Date</th>
                  <th>Contact</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="6" className="sales-empty-cell">
                      Loading clients...
                    </td>
                  </tr>
                ) : filteredClients.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="sales-empty-cell">
                      No clients found.
                    </td>
                  </tr>
                ) : (
                  filteredClients.map((client) => (
                    <tr
                      key={client.id}
                      className="sales-click-row"
                      onClick={() => navigate(`/client-profile/${client.id}`)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>
                        <div className="sales-primary-cell">
                          <div className="sales-name">
                            {client.first_name} {client.last_name}
                          </div>
                          <div className="sales-secondary">
                            Client ID: {client.id}
                          </div>
                        </div>
                      </td>

                      <td onClick={(e) => e.stopPropagation()}>
                        <div className="sales-primary-cell">
                          {client.policies?.length ? (
                            client.policies.map((p) => (
                              <Link
                                key={p.id}
                                to={`/client-profile/${client.id}?policyId=${p.id}`}
                                className="sales-name"
                                style={{ textDecoration: 'none' }}
                              >
                                {getCoverageType(p)}
                              </Link>
                            ))
                          ) : (
                            <div className="sales-secondary">—</div>
                          )}
                        </div>
                      </td>

                      <td>
                        <div className="sales-primary-cell" style={{ gap: '6px' }}>
                          {client.policies?.length ? (
                            client.policies.map((p, index) => (
                              <span key={`${p.id}-${index}`} className={statusClass(getPolicyStatus(p))}>
                                {getPolicyStatus(p)}
                              </span>
                            ))
                          ) : (
                            <span className="sales-secondary">—</span>
                          )}
                        </div>
                      </td>

                      <td>
                        <div className="sales-primary-cell">
                          {client.policies?.length ? (
                            client.policies.map((p, index) => (
                              <div key={`${p.id}-effective-${index}`} className="sales-secondary">
                                {getEffectiveDate(p)}
                              </div>
                            ))
                          ) : (
                            <div className="sales-secondary">—</div>
                          )}
                        </div>
                      </td>

                      <td>
                        <div className="sales-primary-cell">
                          {client.policies?.length ? (
                            client.policies.map((p, index) => (
                              <div key={`${p.id}-termination-${index}`} className="sales-secondary">
                                {getTerminationDate(p)}
                              </div>
                            ))
                          ) : (
                            <div className="sales-secondary">—</div>
                          )}
                        </div>
                      </td>

                      <td onClick={(e) => e.stopPropagation()}>
                        <div className="sales-primary-cell">
                          <div className="sales-name">
                            {client.phone ? (
                              <a
                                href={`tel:${client.phone}`}
                                className="sales-name"
                                style={{ textDecoration: 'none' }}
                              >
                                {client.phone}
                              </a>
                            ) : (
                              'No phone'
                            )}
                          </div>
                          <div className="sales-secondary">
                            {client.email ? (
                              <a
                                href={`mailto:${client.email}`}
                                className="sales-secondary"
                                style={{ textDecoration: 'none' }}
                              >
                                {client.email}
                              </a>
                            ) : (
                              'No email'
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}

export default Clients;